// Mount/unmount transition for Customize-mode chrome (tool groups, the section
// pencil, the Reset button). Unlike a plain `{editMode && …}`, this keeps its
// child mounted through an exit animation so pressing "Done" animates the
// controls OUT instead of snapping them away. Respects prefers-reduced-motion.
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { usePrefersReducedMotion } from '@clickguard/design-system'

const DUR = 220

// The reduced-motion hook is canonical in the DS since 0.18.0 — re-exported so
// the seven sibling importers keep their './customize-reveal' path.
export { usePrefersReducedMotion } from '@clickguard/design-system'

export const CustomizeReveal = ({
	show,
	delay = 0,
	className,
	children
}: {
	show: boolean
	/** Stagger the entrance (ms) so a row of controls cascades in. */
	delay?: number
	className?: string
	children: ReactNode
}) => {
	const reduced = usePrefersReducedMotion()
	const [mounted, setMounted] = useState(show)
	const [phase, setPhase] = useState<'in' | 'out'>(show ? 'in' : 'out')
	const timer = useRef<ReturnType<typeof setTimeout>>()

	useEffect(() => {
		clearTimeout(timer.current)
		if (show) {
			setMounted(true)
			setPhase('in')
		} else {
			setPhase('out')
			timer.current = setTimeout(() => setMounted(false), reduced ? 0 : DUR + delay)
		}
		return () => clearTimeout(timer.current)
		// Intentionally keyed on `show` only — `delay`/`reduced` are read at
		// fire time, not deps for re-running the mount/unmount transition.
	}, [show])

	if (!mounted) return null

	const style: CSSProperties = reduced
		? {}
		: {
				animation: `${phase === 'in' ? 'cgToolIn' : 'cgToolOut'} ${DUR}ms cubic-bezier(0.16, 1, 0.3, 1) both`,
				animationDelay: `${delay}ms`
		  }

	return (
		<span className={className} style={style}>
			{children}
		</span>
	)
}
